// @flow

import React, { Component } from "react";
import { noop, get, isEqual } from "lodash";
import type { UnaryFn, TernaryFn, NullaryFn } from "@tvg/types/Functional";
import type { RacePanelLink, RacesActiveBets } from "@fdr/types/Race";
import type { UserOptedInPromos } from "@tvg/types/User";
import buildRaceUrl from "@tvg/formatter/url";
import {
  handleTouchStart,
  handleTouchMove
} from "@tvg/utils/swipeEventHandlers";
import Icon from "@fdr/static-ui/Icons";
import {
  arrowRight,
  funnel,
  refresh,
  tracks as tracksIcon
} from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";

import Text from "../../_atoms/Text";
import Info from "../../_molecules/Info";
import RaceInfoPanel from "../../_molecules/RaceInfoPanel";
import {
  Title,
  UpperSection,
  ListContainer,
  IconContainer,
  LinkContainer,
  UpcomingRacesWrapper,
  UpcomingContainer,
  LinkDiv,
  NoRacesContainer
} from "./styled-components";

type Props = {
  /**
   * Array of races
   */
  races: RacePanelLink[],
  /**
   * Callback when the race in Track is clicked
   */
  onRaceClick: TernaryFn<
    RacePanelLink,
    string,
    string | UserOptedInPromos,
    void
  >,
  /**
   * Callback when the upcoming races container is being scrolled by swiping
   */
  onContainerSwipe: UnaryFn<number, void>,
  /**
   * Whenever the component is loading
   */
  isLoading: boolean,
  /**
   * Enables the promo badge on race link
   */
  hasPromoBadge: boolean,
  /**
   * Object with the user opted-in promos
   */
  optedInPromos: UserOptedInPromos,
  /**
   * The device this is displaying on
   */
  device: "mobile" | "tablet",
  /**
   * Title for upper section
   */
  title?: string,
  /**
   * Subtitle for upper section
   */
  subtitle?: string,
  /**
   * Flag to see if the "All Races" button should be rendered
   */
  renderAllRacesBtn: boolean,
  /**
   * Should flag the empty state messages of the component
   */
  shouldRenderEmptyMessages: boolean,
  /**
   * Should flag the error empty state messages
   */
  hasError: boolean,
  /**
   * onClearFiltersCallBack
   */
  onClearFilters: NullaryFn<void>,
  /**
   * Tells if there are active filters
   */
  hasActiveFilters: boolean,

  isOpeningApp: boolean,
  redirectToApp: NullaryFn<void>,
  racesActiveBets: RacesActiveBets,
  onSeeAllRaces: NullaryFn<void>,
  useIsPromoTagShownFlag: boolean,
  showHeader?: boolean
};

export default class UpcomingRaces extends Component<Props> {
  static defaultProps = {
    races: [],
    onRaceClick: noop,
    onContainerSwipe: noop,
    isLoading: false,
    hasPromoBadge: false,
    optedInPromos: {},
    device: "mobile",
    title: "Upcoming Races",
    renderAllRacesBtn: true,
    shouldRenderEmptyMessages: false,
    hasError: false,
    onClearFilters: noop,
    isOpeningApp: false,
    hasActiveFilters: false,
    redirectToApp: noop,
    racesActiveBets: {},
    onSeeAllRaces: noop,
    useIsPromoTagShownFlag: false,
    showHeader: true
  };

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  evaluateReachingEdge = (e: SyntheticTouchEvent<*>) => {
    const scrollableElement = e.currentTarget;
    let edgeHasBeenReached = false;
    if (scrollableElement) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollableElement;
      edgeHasBeenReached =
        scrollLeft === 0 || scrollLeft + clientWidth === scrollWidth;
    }

    return edgeHasBeenReached;
  };

  handleSwipeGTMEvents = (e: SyntheticTouchEvent<*>) => {
    let swipeDirection = handleTouchMove(e);
    const isLivingOnTheEdge = this.evaluateReachingEdge(e);

    swipeDirection = isLivingOnTheEdge ? 0 : swipeDirection;
    if (swipeDirection !== 0) {
      this.props.onContainerSwipe(swipeDirection);
    }
  };

  preventDefault = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    this.props.redirectToApp();
  };

  renderRacesList = () =>
    this.props.races.map((race) => {
      const isFeatured =
        get(race, "highlighted.style", false) === "Highlighted";
      const betsCounter = get(
        this.props,
        `racesActiveBets.${race.tvgRaceId}`,
        0
      );
      const raceCode: string = race.raceId ? race.raceId : String(race.id);

      return (
        <UpcomingContainer
          isFeatured={isFeatured}
          data-qa-label={`upcomingRaces-${raceCode}`}
          key={`upcomingRaces-${raceCode}`}
          isOpeningApp={this.props.isOpeningApp}
          onClick={(e) => this.props.isOpeningApp && this.preventDefault(e)}
        >
          <RaceInfoPanel
            race={race}
            raceCardUrl={buildRaceUrl(
              race.trackCode,
              race.trackName,
              +race.raceNumber
            )}
            onCardClick={this.props.onRaceClick}
            optedInPromos={this.props.optedInPromos}
            context="upcomingRaces"
            showActiveBets={betsCounter > 0}
            useIsPromoTagShownFlag={this.props.useIsPromoTagShownFlag}
          />
        </UpcomingContainer>
      );
    });

  renderEmptyStateMessages = () => {
    let title;
    let messageType;
    let upperIconType = "warning";
    let buttonText = null;
    let icon = refresh;
    if (this.props.hasError) {
      title = "Uh Oh!";
      icon = refresh;
      messageType = "There seems to be a problem loading the upcoming races";
      upperIconType = "error";
      buttonText = "Refresh";
    } else if (this.props.hasActiveFilters) {
      title = "No results";
      icon = funnel;
      messageType = "Your filters have hidden the upcoming races";
      buttonText = "Clear Filters";
    } else {
      icon = tracksIcon;
      title = "No more races available today";
      messageType = "Races should become available in the next minutes";
    }

    return (
      <Info
        icon={icon}
        title={title}
        message={messageType}
        hasUpperIcon
        upperIconType={upperIconType}
        buttonText={buttonText}
        onClearFilters={this.props.onClearFilters}
      />
    );
  };

  renderLastElement = () => (
    <LinkDiv
      onClick={this.props.onSeeAllRaces}
      data-qa-label="upcomingLinkContainerLink"
    >
      <IconContainer data-qa-label="upcomingLinkContainerIconContainer">
        <Icon icon={arrowRight} size={24} color={buildColor("blue", "D1")} />
      </IconContainer>
      See all races
    </LinkDiv>
  );

  render() {
    const showNoMoreRacesInfo =
      !this.props.isLoading &&
      !this.props.races.length &&
      !this.props.renderAllRacesBtn;
    const showErrorInfo =
      !this.props.isLoading &&
      this.props.hasError &&
      this.props.shouldRenderEmptyMessages;
    return (
      <UpcomingRacesWrapper
        showHeader={this.props.showHeader}
        data-qa-label="upcomingRacesContainer"
      >
        {this.props.showHeader && (
          <UpperSection data-qa-label="upcomingRaces-top">
            {this.props.title && <Title>{this.props.title}</Title>}
            {this.props.subtitle && (
              <Text tag="p" color={buildColor("grey", "D4")} fontSize={12}>
                {this.props.subtitle}
              </Text>
            )}
          </UpperSection>
        )}
        {
          // If the renderAllRacesBtn is true we will show the button
          // instead of rendering the empty case message
          ((showNoMoreRacesInfo || showErrorInfo) &&
            this.renderEmptyStateMessages()) ||
            (!this.props.isLoading && this.props.races.length === 0 ? (
              <NoRacesContainer>{this.renderLastElement()}</NoRacesContainer>
            ) : (
              <ListContainer
                data-qa-label="upcomingRacesListContainer"
                onTouchStart={handleTouchStart}
                onTouchMove={this.handleSwipeGTMEvents}
              >
                {this.renderRacesList()}
                {this.props.renderAllRacesBtn && (
                  <LinkContainer data-qa-label="upcomingLinkContainer">
                    {this.renderLastElement()}
                  </LinkContainer>
                )}
              </ListContainer>
            ))
        }
      </UpcomingRacesWrapper>
    );
  }
}
